'use client';

import { useState, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import styles from './restaurant.module.css';

const steps = [
  {
    num: '01',
    title: 'Share Your Restaurant Details',
    desc: 'Upload your menu, hours, and location — or drop in your website URL and we extract everything automatically.',
    illus: (
      <div className={styles.illusStack}>
        <div className={`${styles.illusDoc} ${styles.illusDocBack2}`}>
          <span>yourrestaurant.com</span>
        </div>
        <div className={`${styles.illusDoc} ${styles.illusDocBack1}`}>
          <span>Opening Hours</span>
        </div>
        <div className={`${styles.illusDoc} ${styles.illusDocFront}`}>
          <span className={styles.illusDocIcon}>📋</span>
          <span>Menu.pdf</span>
        </div>
        <div className={styles.illusAgent}>
          <span>🤖</span>
          <span>Restaurant Agent</span>
        </div>
      </div>
    ),
  },
  {
    num: '02',
    title: 'Train Your Agent',
    desc: 'Name your agent, customize its greeting and tone, define how reservations are handled, and set language preferences.',
    illus: (
      <div className={styles.illusPanel}>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Agent Name</span>
          <span className={styles.illusPanelValue}>Aria</span>
        </div>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Tone</span>
          <span className={styles.illusPanelValue}>Warm &amp; Professional</span>
        </div>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Language</span>
          <span className={styles.illusPanelValue}>English · Spanish</span>
        </div>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Reservations</span>
          <span className={`${styles.illusPanelValue} ${styles.illusPanelOn}`}>Enabled ✓</span>
        </div>
      </div>
    ),
  },
  {
    num: '03',
    title: 'Claim Your Phone Number',
    desc: 'Choose a dedicated local number for your restaurant. Forward your existing number or start using the new one immediately.',
    illus: (
      <div className={styles.illusPhone}>
        <div className={styles.illusPhoneLabel}>Your Local Number</div>
        <div className={styles.illusPhoneNum}>(555) 867-5309</div>
        <div className={styles.illusPhoneBadge}>✓ Ready to receive calls</div>
      </div>
    ),
  },
  {
    num: '04',
    title: 'Go Live 24/7',
    desc: 'Your restaurant agent is on. Every call answered, every reservation captured — no hold times, no missed calls.',
    illus: (
      <div className={styles.illusLive}>
        <div className={styles.illusLiveBadge}>
          <span className={styles.illusLiveDot} />
          LIVE
        </div>
        <ul className={styles.illusLiveList}>
          <li>No hold times</li>
          <li>No missed reservations</li>
          <li>No extra staff needed</li>
        </ul>
      </div>
    ),
  },
];

export default function RestaurantAgentPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const goTo = (idx: number) => {
    const clamped = Math.max(0, Math.min(idx, steps.length - 1));
    setCurrentSlide(clamped);
    const vp = viewportRef.current;
    if (!vp) return;
    const cards = vp.querySelectorAll('[data-card]');
    cards[clamped]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  };

  return (
    <div className={styles.page}>

      {/* ── Navigation ── */}
      <NavBar />

      {/* ── Hero ── */}
      <section className={styles.hero}>
        <Image
          src="/images/restaurant.png"
          alt="Restaurant background"
          fill
          priority
          className={styles.heroImage}
        />
        <div className={styles.heroOverlay} />
        <div className={styles.heroLayout}>
          <div className={styles.heroContent}>
            <span className={styles.heroTag}>Restaurant AI Voice Agent</span>
            <h1 className={styles.heroTitle}>Always On. Never Late. Never Calls in Sick.</h1>
            <p className={styles.heroSubtitle}>
              Your AI agent answers every call, takes orders, manages reservations, and handles customer inquiries — around the clock, no exceptions.
            </p>
            <div className={styles.heroBtns}>
              <Link href="/register?industry=restaurant" className={styles.heroBtnPrimary}>Get Started</Link>
              <Link href="#how-it-works" className={styles.heroBtnSecondary}>See How It Works</Link>
            </div>
          </div>

          {/* ── Live Demo Widget ── */}
          <div className={styles.audioWidget}>
            <div className={styles.audioWidgetHeader}>
              <div className={styles.audioAvatar}>AI</div>
              <div>
                <div className={styles.audioAgentName} style={{ marginLeft: '44px' }}>Aria</div>
                <div className={styles.audioAgentStatus}>
                  <span className={styles.audioStatusDotActive} />
                  Live · Restaurant AI Agent
                </div>
              </div>
            </div>
            <div className={styles.audioWaveform}>
              {[...Array(16)].map((_, i) => (
                <span key={i} className={`${styles.audioWaveBar} ${styles.audioWaveBarActive}`} style={{ animationDelay: `${i * 0.07}s` }} />
              ))}
            </div>
            <a href="tel:6183481765" className={styles.audioPlayBtn} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
              </svg>
              618-348-1765
            </a>
            <p className={styles.audioLabel}>Tap to call · Available 24/7</p>
          </div>

        </div>
      </section>

      {/* ── Features ── */}
      <section className={styles.featuresSection}>
        <div className={styles.featuresInner}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>INSTANT, INTELLIGENT, AND ALWAYS ON</span>
            <h2>Every Call Answered.<br />Every Order Captured.<br />Every Time.</h2>
            <p>NovaMind AI handles the phones so your team doesn&apos;t have to — taking orders, booking reservations, and answering customer questions 24/7 with zero hold times.</p>
          </div>
          <div className={styles.featuresGrid}>

            {/* Card 1 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusCallWrap}>
                  <div className={styles.illusCallBadge}>
                    <span className={styles.illusCallDot} /> Live · Incoming Call
                  </div>
                  <div className={styles.illusCallNumber}>(555) 234-5678</div>
                  <div className={styles.illusWaveBars}>
                    {[...Array(12)].map((_, i) => (
                      <span key={i} className={styles.illusWaveBar} style={{ animationDelay: `${i * 0.08}s` }} />
                    ))}
                  </div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Instant Call Answering & Routing</h3>
                <p className={styles.fcSubtitle}>AI answers 100% of inbound calls and routes to the right person with zero hold time.</p>
              </div>
            </div>

            {/* Card 2 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusOrderSlip}>
                  <div className={styles.illusOrderTitle}>New Order · Takeout</div>
                  <div className={styles.illusOrderRow}><span>Burger + Fries</span><span>$14.99</span></div>
                  <div className={styles.illusOrderRow}><span>Caesar Salad</span><span>$9.99</span></div>
                  <div className={styles.illusOrderRow}><span>Diet Coke</span><span>$2.99</span></div>
                  <div className={styles.illusOrderTotal}><span>Total</span><span>$27.97</span></div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Phone Orders</h3>
                <p className={styles.fcSubtitle}>Takes full takeout and delivery orders over the phone, every time.</p>
              </div>
            </div>

            {/* Card 3 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusReservCard}>
                  <div className={styles.illusReservTitle}>New Reservation</div>
                  <div className={styles.illusReservRow}><span>Name</span><span>Sarah Johnson</span></div>
                  <div className={styles.illusReservRow}><span>Date</span><span>Sat, Mar 29</span></div>
                  <div className={styles.illusReservRow}><span>Time</span><span>7:30 PM</span></div>
                  <div className={styles.illusReservRow}><span>Party</span><span>4 guests</span></div>
                  <div className={styles.illusReservConfirm}>✓ Confirmed</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Reservation Management</h3>
                <p className={styles.fcSubtitle}>Books, confirms, modifies, and cancels reservations automatically.</p>
              </div>
            </div>

            {/* Card 4 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusStatusWrap}>
                  <div className={styles.illusStatusBadge}>📦 Order #1042</div>
                  <div className={styles.illusStatusMsg}>Your order is <strong>ready for pickup!</strong></div>
                  <div className={styles.illusStatusEst}>Estimated wait: <span>5 min</span></div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Order Status Updates</h3>
                <p className={styles.fcSubtitle}>Tells customers their order status and estimated pickup or delivery time.</p>
              </div>
            </div>

            {/* Card 5 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusEventCard}>
                  <div className={styles.illusEventTitle}>Private Event Request</div>
                  <div className={styles.illusEventRow}><span>Event</span><span>Birthday Dinner</span></div>
                  <div className={styles.illusEventRow}><span>Guests</span><span>30 people</span></div>
                  <div className={styles.illusEventRow}><span>Date</span><span>Apr 12, 8:00 PM</span></div>
                  <div className={styles.illusEventRow}><span>Room</span><span>Private Dining</span></div>
                  <div className={styles.illusEventBadge}>✓ Inquiry Captured</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Private Events & Catering</h3>
                <p className={styles.fcSubtitle}>Handles inquiries and bookings for private dining and catering events.</p>
              </div>
            </div>

            {/* Card 6 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusLangWrap}>
                  {[
                    { lang: 'EN', text: 'Table for two, please.' },
                    { lang: 'ES', text: 'Mesa para dos, por favor.' },
                    { lang: 'FR', text: "Une table pour deux, s'il vous plaît." },
                  ].map((b, i) => (
                    <div key={i} className={styles.illusLangBubble}>
                      <span className={styles.illusLangTag}>{b.lang}</span>
                      <span className={styles.illusLangText}>{b.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Multi-language on Request</h3>
                <p className={styles.fcSubtitle}>Configured for the languages your customers speak — add support based on your needs.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className={styles.howItWorksSection}>
        <div className={styles.howItWorksHeader}>
          <div>
            <h2 className={styles.howTitle}>
              <em className={styles.howTitleItalic}>How</em> It Works.
            </h2>
            <p className={styles.howSubtitle}>From sign-up to live calls in under 5 minutes</p>
          </div>
          <div className={styles.sliderArrows}>
            <button
              className={styles.arrowBtn}
              onClick={() => goTo(currentSlide - 1)}
              disabled={currentSlide === 0}
              aria-label="Previous"
            >
              ‹
            </button>
            <button
              className={styles.arrowBtn}
              onClick={() => goTo(currentSlide + 1)}
              disabled={currentSlide === steps.length - 1}
              aria-label="Next"
            >
              ›
            </button>
          </div>
        </div>

        <div className={styles.sliderViewport} ref={viewportRef}>
          {steps.map((step, i) => (
            <div
              key={i}
              data-card
              className={`${styles.slideCard} ${styles[`slideCard${i + 1}`]}`}
            >
              <span className={styles.slideNum}>{step.num}</span>
              <div className={styles.slideIllus}>{step.illus}</div>
              <div className={styles.slideText}>
                <h3 className={styles.slideTitle}>{step.title}</h3>
                <p className={styles.slideDesc}>{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className={styles.ctaSection}>
        <Image
          src="/images/software_section1.png"
          alt="CTA background"
          fill
          priority
          className={styles.ctaImage}
        />
        <div className={styles.ctaOverlay} />
        <div className={styles.ctaInner}>
          <h2 className={styles.ctaTitle}>Ready to Transform Your Restaurant?</h2>
          <p className={styles.ctaSubtitle}>Orders, reservations, and delivery requests handled instantly — your AI host never puts a customer on hold or lets a call go to voicemail.</p>
          <Link href="/register?industry=restaurant" className={styles.ctaBtn}>Get Started</Link>
        </div>
      </section>

      {/* ── Footer ── */}
      <Footer />

    </div>
  );
}
